/** @jsxImportSource @emotion/react */
import { ApplyPaymentModal } from 'components/lab/billingEraReports/ApplyPaymentModal'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import React, { useState } from 'react'
import { BillingTransactionClaim, NeedsWorkItemDto } from 'store/types'
import { NeedsWorkFlagModal } from './NeedsWorkFlagModal'
import { NeedsWorkNotesModal } from './NeedsWorkNotesModal'
import { NeedsWorkResolveModal } from './NeedsWorkResolveModal'

// The posting form reads only the claim's id and the payer's claim number.
const claimForPostingForm = (item: NeedsWorkItemDto): Pick<BillingTransactionClaim, 'id' | 'payerClaimControlNumber'> => ({
  id: item.remit?.claimId ?? '',
  payerClaimControlNumber: item.remit?.payerClaimControlNumber ?? null,
})

/**
 * What can be done to an item from wherever it is shown — edit it, resolve it
 * (deciding about a parked remit, which may continue in the posting form), or
 * open its notes — and the dialogs that do it.
 */
export const useNeedsWorkActions = () => {
  const canModify = useLabPermission(UserPermission.MODIFY_BILLING_CHARGES)
  const canPost = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [editing, setEditing] = useState<NeedsWorkItemDto | null>(null)
  const [resolving, setResolving] = useState<NeedsWorkItemDto | null>(null)
  const [notesFor, setNotesFor] = useState<NeedsWorkItemDto | null>(null)
  const [correcting, setCorrecting] = useState<{ item: NeedsWorkItemDto; note: string } | null>(null)

  // Deciding about a parked remit takes posting rights.
  const canResolve = (item: NeedsWorkItemDto): boolean => (item.remit?.parked ? canPost : canModify)

  const modals = (
    <>
      {editing && <NeedsWorkFlagModal target={{ kind: 'edit', item: editing }} onClose={() => setEditing(null)} />}
      {resolving && (
        <NeedsWorkResolveModal
          item={resolving}
          onClose={() => setResolving(null)}
          onPostWithCorrections={(note) => setCorrecting({ item: resolving, note })}
        />
      )}
      {notesFor && <NeedsWorkNotesModal item={notesFor} onClose={() => setNotesFor(null)} />}
      {correcting?.item.remit?.eraId && (
        <ApplyPaymentModal
          open
          onClose={() => setCorrecting(null)}
          claim={claimForPostingForm(correcting.item)}
          eraId={correcting.item.remit.eraId}
          claimId={correcting.item.remit.claimNumber || correcting.item.remit.claimId}
          resolveNeedsWork={{ itemId: correcting.item.id, note: correcting.note }}
        />
      )}
    </>
  )

  return {
    canModify,
    canResolve,
    edit: setEditing,
    resolve: setResolving,
    notes: setNotesFor,
    modals,
  }
}
